<template>
  <div class="story-overlay">
    <div
      ref="overlayRef"
      class="story-overlay__overlay"
      :class="{'story-overlay__overlay--visible': storiesStore.currentStory}"
    >
      <div class="story-overlay__inner">
        <iframe
          title="story"
          class="story-overlay__iframe"
          frameborder="0"
          :src="storiesStore.currentStory?.tappable_url"
        />
        <button class="story-overlay__button" @click="onClose">
          <AppIconLoader
            name="close"
            aria-hidden="true"
            variant="large"
            class="story-overlay__icon"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStoriesStore } from '~/store/stories'
import { generateSlugOrIdFilter, joinSlugId } from '~/utils/slug-id'
import { getCollection } from '~/services/custom-endpoints'
import { usePageStore } from '~/store/page'
import { useHeadData, useTracking } from '#imports'
import { usePopState } from '~/composables/usePopState'

const storiesStore = useStoriesStore()
const pageStore = usePageStore()

const router = useRouter()
const overlayRef = ref<HTMLElement | null>(null)
const isActive = ref(false)

const onClose = function (updateHistory = true) {
  storiesStore.clear()
  document.body.style.overflow = 'auto'
  if (updateHistory) {
    history.pushState({ story: undefined }, '', location.pathname)
  }
}

onMounted(() => {
  const storySlugId = router.currentRoute.value.query?.story

  if (storySlugId) {
    // next tick needed for queryParam on loading page.
    nextTick(async () => {
      const filter = generateSlugOrIdFilter(storySlugId.toString())

      const { data: result, error } = await getCollection(
        'story_page',
        { status: 'published', ...filter },
        ['*', 'domain.*.*', 'belongs_to.*.*']
      )

      if (error.value) {
        throw new Error(error.value.message)
      }
      if (result.value) {
        storiesStore.setStory(result.value.data[0])
      }
    })
  }
})

useFocusTrap(overlayRef, isActive)

watch(storiesStore.$state, (newValue) => {
  if (newValue.currentStory && newValue.currentStory.tappable_url) {
    isActive.value = true

    document.body.style.overflow = 'hidden'
    history.pushState({ story: newValue.currentStory.id }, '', `${location.pathname}?story=${joinSlugId(newValue.currentStory)}`)
  } else {
    isActive.value = false
  }
})

// update the head data & analytics
watch(storiesStore.$state, (newValue) => {
  const curPage = (newValue.currentStory) ? newValue.currentStory : pageStore.getCurrentPage
  useHeadData(curPage)
  useTracking(curPage)
})

usePopState(() => {
  if (storiesStore.currentStory) {
    onClose()
  }
})

</script>

<style scoped lang="postcss">
.story-overlay {
  &__overlay {
    position: fixed;
    z-index: -40;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    opacity: 0;
    transition: opacity .3s ease-in;

    &--visible {
      opacity: 1;
      pointer-events: all;
      z-index: 40;
    }
  }

  &__inner {
    position: relative;
    height: 100%;
    width: 100%;
  }

  &__iframe {
    height: 100%;
    width: 100%;
  }

  &__button {
    position: absolute;
    left: 1rem;
    top: 0.5rem;
    z-index: 50;
    cursor: pointer;

    @media (--lg) {
      left: auto;
      right: 3rem;
      top: 2.5rem;
    }
  }

  &__icon {
    color: var(--color-blue-aqua);
  }
}
</style>
